import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronDown, ChevronUp, Plus } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { chatsPollInterval, createCrmCard, listCrmCards, listFunnels } from '@/entities/chats';
import { ApiError } from '@/shared/api';
import { crmName, useI18n } from '@/shared/i18n';
import { cn } from '@/shared/lib/utils';
import { Button } from '@/shared/ui/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/shadcn/dropdown-menu';
import { Input } from '@/shared/ui/shadcn/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/shadcn/select';
import { CrmBoard } from '@/widgets/crm-board';
import { HeaderAction, PageHeader } from '@/widgets/page-header';

function sameStage(left: string, right: string) {
  return left.trim().toLowerCase() === right.trim().toLowerCase();
}

export function CrmPage() {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [menuOpen, setMenuOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [stage, setStage] = useState('');
  const [budget, setBudget] = useState('');

  const funnelsQuery = useQuery({
    queryKey: ['crm-funnels'],
    queryFn: listFunnels,
  });
  const funnels = funnelsQuery.data ?? [];
  const requested = params.get('pipeline') ?? '';
  const pipeline = funnels.some((funnel) => funnel.name === requested)
    ? requested
    : (funnels[0]?.name ?? '');
  const funnel = funnels.find((item) => item.name === pipeline);
  const cardsQuery = useQuery({
    queryKey: ['crm-cards', pipeline],
    queryFn: () => listCrmCards(pipeline),
    enabled: Boolean(pipeline),
    refetchInterval: chatsPollInterval,
  });
  const title = pipeline ? crmName(t.crm.names, pipeline) : t.crm.title;
  const stages = funnel?.stages ?? [];

  const create = useMutation({
    mutationFn: () =>
      createCrmCard({
        pipeline,
        stage: stage || undefined,
        display_name: name.trim(),
        phone: phone.trim() || undefined,
        price: budget.trim() ? Number(budget.replace(/\s/g, '')) || 0 : undefined,
      }),
    onSuccess: async () => {
      setCreateOpen(false);
      setName('');
      setPhone('');
      setStage('');
      setBudget('');
      await queryClient.invalidateQueries({ queryKey: ['crm-cards', pipeline] });
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : t.crm.createFailed);
    },
  });

  const openCreate = () => {
    setName('');
    setPhone('');
    setBudget('');
    setStage(stages[0] ?? '');
    setCreateOpen(true);
  };

  return (
    <div className="absolute inset-0 flex min-h-0 flex-col overflow-hidden">
      <PageHeader
        title={
          <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
            <DropdownMenuTrigger
              disabled={funnels.length === 0}
              render={
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="-ml-2 h-8 max-w-full gap-1 px-2 text-sm font-semibold"
                  aria-label={t.crm.pickFunnel}
                />
              }
            >
              <span className="truncate">{title}</span>
              {menuOpen ? (
                <ChevronUp className="size-4 shrink-0 opacity-70" />
              ) : (
                <ChevronDown className="size-4 shrink-0 opacity-70" />
              )}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-56">
              {funnels.map((item) => {
                const selected = item.name === pipeline;
                return (
                  <DropdownMenuItem
                    key={item.name}
                    className={cn(selected && 'bg-accent')}
                    onClick={() => setParams({ pipeline: item.name })}
                  >
                    <span className="min-w-0 flex-1 truncate">
                      {crmName(t.crm.names, item.name)}
                    </span>
                    {selected ? <Check className="size-4 shrink-0" /> : null}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        }
        description={t.crm.description}
        actions={
          <HeaderAction
            label={t.crm.addCard}
            disabled={!funnel || create.isPending}
            onClick={openCreate}
          >
            <Plus className="size-4" />
          </HeaderAction>
        }
      />
      <div className="min-h-0 flex-1">
        {funnel ? <CrmBoard funnel={funnel} cards={cardsQuery.data ?? []} /> : null}
      </div>

      <Dialog
        open={createOpen}
        onOpenChange={(open) => {
          if (!open && !create.isPending) setCreateOpen(false);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t.crm.createTitle}</DialogTitle>
            <DialogDescription>{t.crm.createHint}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <Input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={t.crm.namePlaceholder}
              autoFocus
            />
            <Input
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              placeholder={t.crm.phonePlaceholder}
            />
            <Input
              value={budget}
              onChange={(event) => setBudget(event.target.value)}
              placeholder={t.crm.budgetPlaceholder}
              inputMode="numeric"
            />
            {stages.length > 0 ? (
              <Select
                value={stages.find((item) => sameStage(item, stage)) ?? stages[0]}
                onValueChange={(value) => {
                  if (value) setStage(value);
                }}
              >
                <SelectTrigger className="w-full" aria-label={t.common.stage}>
                  <SelectValue>{(value) => crmName(t.crm.names, value)}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {stages.map((item) => (
                    <SelectItem key={item} value={item}>
                      {crmName(t.crm.names, item)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={create.isPending}
              onClick={() => setCreateOpen(false)}
            >
              {t.crm.cancel}
            </Button>
            <Button
              type="button"
              disabled={create.isPending || name.trim() === '' || !pipeline}
              onClick={() => create.mutate()}
            >
              {create.isPending ? t.common.creating : t.crm.create}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
